import React, { useRef, useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';

function getRoundedFloatWithPrecision(num, precision) {
  if (precision === void 0) {
    precision = 2;
  }

  var ROUND_UPTO = Math.pow(10, precision);
  return Math.round((num + Number.EPSILON) * ROUND_UPTO) / ROUND_UPTO;
}
function getNextStepValue(currentStepValue, toValue, step) {
  if (currentStepValue < toValue) {
    return currentStepValue += Math.min(Math.abs(step), toValue - currentStepValue);
  } else {
    return currentStepValue -= Math.min(Math.abs(step), toValue + currentStepValue);
  }
}
function calcFrequency(currentNumber, toValue, timeout, step) {
  return timeout * step / (Math.abs(currentNumber - toValue) || 1);
}

var NumberScroller = function NumberScroller(_ref) {
  var _ref$decimalPlaces = _ref.decimalPlaces,
      decimalPlaces = _ref$decimalPlaces === void 0 ? 0 : _ref$decimalPlaces,
      _ref$delay = _ref.delay,
      delay = _ref$delay === void 0 ? 0 : _ref$delay,
      renderFrequency = _ref.renderFrequency,
      _ref$from = _ref.from,
      from = _ref$from === void 0 ? 0 : _ref$from,
      toLocaleStringProps = _ref.toLocaleStringProps,
      _ref$step = _ref.step,
      step = _ref$step === void 0 ? 1 : _ref$step,
      _ref$timeout = _ref.timeout,
      timeout = _ref$timeout === void 0 ? 1000 : _ref$timeout,
      _ref$to = _ref.to,
      to = _ref$to === void 0 ? 0 : _ref$to;

  var _isMounted = useRef();

  var _isCounting = useRef();

  var _renderFrequency = useRef(renderFrequency);

  var _timer = useRef(null);

  var _useState = useState(from),
      currentNumber = _useState[0],
      setCurrentNumber = _useState[1];

  var runEngine = useCallback(function (currentNumber) {
    if (_isMounted.current && !isNaN(currentNumber) && currentNumber !== to) {
      _timer.current = setTimeout(function () {
        var changedValue = getNextStepValue(currentNumber, to, step);
        runEngine(changedValue);
        setCurrentNumber(changedValue); // update UI
      }, _renderFrequency.current);
    } else {
      // indicate the counting engine has finished
      _isCounting.current = false;
    }
  }, [step, to]);
  useEffect(function () {
    _isMounted.current = true; // if no custom renderFrequency calculate difference between currentNumber and new 'to' number

    if (!renderFrequency) {
      _renderFrequency.current = calcFrequency(currentNumber, to, timeout, step);
    } // if the previous runEngine recursion is still going, cancel it so the new one can start


    if (_isCounting.current) {
      clearTimeout(_timer.current);
    } // start new counting engine with or without delay


    _isCounting.current = true;
    delay ? setTimeout(function () {
      return runEngine(currentNumber);
    }, delay) : runEngine(currentNumber); // cancel running the engine when component unmounts (prevent memory leak)

    return function () {
      setCurrentNumber(to);
      _isMounted.current = false;
    }; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [delay, renderFrequency, runEngine, timeout, to]);
  var renderedNumber = !isNaN(currentNumber) ? currentNumber : 0;
  return React.createElement(React.Fragment, null, toLocaleStringProps ? renderedNumber.toLocaleString.apply(renderedNumber, toLocaleStringProps) : getRoundedFloatWithPrecision(renderedNumber, decimalPlaces));
};
NumberScroller.propTypes = {
  decimalPlaces: PropTypes.number,
  delay: PropTypes.number,
  from: PropTypes.number,
  toLocaleStringProps: PropTypes.any,
  renderFrequency: PropTypes.number,
  step: PropTypes.number,
  timeout: PropTypes.number,
  to: PropTypes.number
};

export default NumberScroller;
export { NumberScroller };
